import { isProcessingLike, type NoteStatus } from '../notes/status';

/** First poll delay, and every one after a status change resets to this. */
export const POLL_BASE_MS = 3_000;
/** The cadence never grows past this, however long a note stays processing-like. */
export const POLL_MAX_MS = 20_000;
/** Each missed change multiplies the wait by this - a phone on a slow connection is not woken
 * up every three seconds forever for a recognition run that takes a minute. */
export const POLL_BACKOFF_FACTOR = 1.6;

/** Whether any of these rows is in a status the system can still move on its own
 * (`notes/status.ts`'s `isProcessingLike`). This is the stop condition: once it is `false`,
 * nothing on the page needs to be polled. */
export function hasProcessingLikeNote(rows: readonly { readonly status: NoteStatus }[]): boolean {
  return rows.some((row) => isProcessingLike(row.status));
}

/** The delay before the next poll, `attempt` polls after the cadence last reset. Bounded and
 * monotonically non-decreasing, capped at `POLL_MAX_MS`. */
export function nextPollDelayMs(attempt: number): number {
  const raw = POLL_BASE_MS * POLL_BACKOFF_FACTOR ** Math.max(attempt, 0);
  return Math.min(Math.round(raw), POLL_MAX_MS);
}

/**
 * The interval a poller should use next, or `false` to stop entirely.
 *
 * Pure and independent of any particular query library, so the stop condition and the backoff
 * schedule are each provable without mounting a component or driving fake timers.
 */
export function computeRefetchIntervalMs(
  rows: readonly { readonly status: NoteStatus }[],
  attempt: number,
): number | false {
  if (!hasProcessingLikeNote(rows)) {
    return false;
  }
  return nextPollDelayMs(attempt);
}
